
import {
    ChipOption,
    computed,
    defineComponent,
    nextTick,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    watch
} from 'vue'
import GoodRoadBetBox from './good-road-bet-box.vue'
import RoadMap from '@/components/roadmap/index.vue'
import { useStore } from '@/store'
import { AppMutation } from '@/store/types'
import { BetType, RESULT } from '@/types/global'
import { useI18n } from 'vue-i18n'
import { saveBet } from '@/api'
import audioPlayer, { SOUNDKEY } from '@/utils/sounds'
import { getMemberSideBetAmounts, GoodRoadTableEntry } from './utils'

export default defineComponent({
    name: 'GoodRoadItem',
    components: {
        'good-road-bet-box': GoodRoadBetBox,
        'road-map': RoadMap
    },
    props: {
        // Define props here if needed
        mData: {
            type: Object as PropType<GoodRoadTableEntry>,
            required: false
        },
        isFreeBaccarat: {
            type: Boolean,
            default: false
        }
    },
    setup(props, { emit }) {
        const store = useStore()
        const { t } = useI18n()

        const pBet = ref<number>(0)
        const bBet = ref<number>(0)
        const tempPBet = ref<number>(0)
        const tempBBet = ref<number>(0)
        const isBetOnProcess = ref(false)
        const allowBet = computed(() => tempBBet.value + tempPBet.value > 0)
        const toastMessage = ref<{ id: number | boolean; message: string }[]>([])
        const bankerBetBox = ref<{
            removeTempUIBet: () => void
            applyTempUIBet: () => void
            resetBetUI: () => void
            hasChips: () => boolean
            getChipsTotal: () => number
            handleOnPlaceChip: (arg: {
                t: number
                amt: number
                type: 'temp' | 'confirmed'
            }) => void
        } | null>(null)
        const playerBetBox = ref<{
            removeTempUIBet: () => void
            applyTempUIBet: () => void
            resetBetUI: () => void
            hasChips: () => boolean
            getChipsTotal: () => number
            handleOnPlaceChip: (arg: {
                t: number
                amt: number
                type: 'temp' | 'confirmed'
            }) => void
        } | null>(null)

        const memberId = computed(() => store.getters['memberid'])
        const token = computed(() => store.getters['token'])
        const userCommandType = computed(() => store.getters['commandType'])
        const isFreeUser = computed((): boolean => store.getters['isFreeUser'])
        const currentChip = computed((): number => store.getters['currentChip'])
        const customChip = computed(() => store.getters['customChip'])
        const chips = computed((): ChipOption => store.getters['chips'])

        const callUpdateCallback = () => {
            const hasConfimedBet = pBet.value + bBet.value > 0
            const hasTempBet = tempPBet.value + tempBBet.value > 0

            emit('on-update', hasConfimedBet || hasTempBet)
        }

        const handleToastMessage = (_message: string) => {
            if (!_message) return

            const _t = {
                id: window.setTimeout(() => {
                    const _tt = toastMessage.value.shift()
                    if (_tt && typeof _tt.id === 'number') {
                        clearTimeout(_tt.id)
                    }
                }, 1500),
                message: _message
            }
            toastMessage.value.push(_t)
        }

        const clearToastMessages = () => {
            toastMessage.value.forEach((toastM) => {
                if (typeof toastM.id === 'number') {
                    clearTimeout(toastM.id)
                    toastM.id = false
                }
            })
            toastMessage.value = []
        }

        onBeforeUnmount(() => {
            clearToastMessages()
            clearCardResultTimer()
        })

        const showCardResult = ref(false)
        const CARD_RESULT_DISPLAY_MS = 4000
        const CARD_RESULT_NEW_GAME_DELAY_MS = 3000
        let cardResultTimer: number | boolean = false
        let cardResultShownAt = 0

        type CardResultSnapshot = {
            blueCards: string[]
            redCards: string[]
            blueTotal: number
            redTotal: number
            whoWin: number
        }

        const cardResultSnapshot = ref<CardResultSnapshot | null>(null)

        const clearCardResultTimer = () => {
            if (typeof cardResultTimer === 'number') {
                clearTimeout(cardResultTimer)
                cardResultTimer = false
            }
        }

        const hideCardResult = () => {
            clearCardResultTimer()
            showCardResult.value = false
            cardResultSnapshot.value = null
            cardResultShownAt = 0
        }

        const scheduleHideCardResult = (delayMs: number) => {
            clearCardResultTimer()
            cardResultTimer = window.setTimeout(() => {
                hideCardResult()
            }, Math.max(0, delayMs))
        }

        const openCardResult = () => {
            const expand = props.mData?.table?.expand
            if (!expand) return

            const nextBlueCards = [
                expand.playerOne,
                expand.playerTwo,
                expand.playerThree
            ].filter((card): card is string => !!card)
            const nextRedCards = [
                expand.bankerOne,
                expand.bankerTwo,
                expand.bankerThree
            ].filter((card): card is string => !!card)

            if (!nextBlueCards.length || !nextRedCards.length) return

            cardResultSnapshot.value = {
                blueCards: nextBlueCards,
                redCards: nextRedCards,
                blueTotal: expand.playerCount ?? 0,
                redTotal: expand.bankerCount ?? 0,
                whoWin: expand.whoWin ?? -1
            }
            showCardResult.value = true
            cardResultShownAt = Date.now()
            scheduleHideCardResult(CARD_RESULT_DISPLAY_MS)
        }

        // Keep showing a bit longer when NEW_GAME clears live card fields
        const delayHideOnNewGame = () => {
            if (!showCardResult.value || !cardResultSnapshot.value) return

            const elapsed = Date.now() - cardResultShownAt
            const remain = CARD_RESULT_DISPLAY_MS - elapsed
            const delay = Math.max(remain, CARD_RESULT_NEW_GAME_DELAY_MS)
            scheduleHideCardResult(delay)
        }

        const liveBlueCards = computed((): string[] => {
            const expand = props.mData?.table?.expand
            if (!expand) return []
            return [expand.playerOne, expand.playerTwo, expand.playerThree].filter(
                (card): card is string => !!card
            )
        })

        const liveRedCards = computed((): string[] => {
            const expand = props.mData?.table?.expand
            if (!expand) return []
            return [expand.bankerOne, expand.bankerTwo, expand.bankerThree].filter(
                (card): card is string => !!card
            )
        })

        const whoWin = computed(
            () => cardResultSnapshot.value?.whoWin ?? -1
        )

        const hasCardResult = computed(() => {
            return liveBlueCards.value.length > 0 && liveRedCards.value.length > 0
        })

        const isBaccarat = computed(
            () => props.mData?.table?.gameTypeNo === 'G002'
        )

        const blueLabel = computed(() =>
            isBaccarat.value ? t('game.player') : t('game.tiger')
        )
        const redLabel = computed(() =>
            isBaccarat.value ? t('game.banker') : t('game.dragon')
        )

        const isBlueWin = computed(() => {
            return (
                whoWin.value === RESULT.BLUE || whoWin.value === RESULT.TIGER
            )
        })
        const isRedWin = computed(() => {
            return (
                whoWin.value === RESULT.RED || whoWin.value === RESULT.DRAGON
            )
        })
        const isTieWin = computed(() => {
            return (
                whoWin.value === RESULT.GREEN ||
                whoWin.value === RESULT.DRAGON_TIGER_TIE ||
                whoWin.value === 5
            )
        })

        const winnerColor = computed(() => {
            if (isRedWin.value) return 1
            if (isTieWin.value) return 2
            if (isBlueWin.value) return 3
            return 0
        })

        const resultLabel = computed(() => {
            if (isRedWin.value) {
                return isBaccarat.value
                    ? `庄赢`
                    : `龙赢`
            }
            if (isBlueWin.value) {
                return isBaccarat.value
                    ? `闲赢`
                    : `虎赢`
            }
            if (isTieWin.value) {
                return `和赢`
            }
            return ''
        })

        const cardClass = (card: string, isThird: boolean) => {
            return isThird ? `h_${card}` : `v_${card}`
        }

        // Banker: 3rd card on the left; Player: 3rd card on the right
        const redCardsDisplay = computed(() => {
            const cards = cardResultSnapshot.value?.redCards || []
            const mapped = cards.map((code, index) => ({
                code,
                isThird: index === 2
            }))
            if (mapped.length < 3) return mapped
            return [mapped[2], mapped[0], mapped[1]]
        })

        const blueCardsDisplay = computed(() => {
            const cards = cardResultSnapshot.value?.blueCards || []
            return cards.map((code, index) => ({
                code,
                isThird: index === 2
            }))
        })

        // Show card result overlay when END_GAME cards arrive
        watch(
            () => ({
                hasCards: hasCardResult.value,
                whoWin: props.mData?.table?.expand?.whoWin ?? -1,
                gameKey: `${props.mData?.table?.expand?.shoeNo}-${props.mData?.table?.expand?.juNo}-${props.mData?.table?.expand?.gameNo}`
            }),
            (next, prev) => {
                if (next.hasCards && next.whoWin !== -1) {
                    if (!prev?.hasCards || next.gameKey !== prev?.gameKey) {
                        openCardResult()
                    }
                    return
                }

                // NEW_GAME / cards cleared: keep snapshot visible with extra delay
                if (prev?.hasCards && !next.hasCards) {
                    delayHideOnNewGame()
                } else if (prev && next.gameKey !== prev.gameKey) {
                    delayHideOnNewGame()
                }
            }
        )

        const resetAllBets = () => {
            pBet.value = 0
            bBet.value = 0
            tempPBet.value = 0
            tempBBet.value = 0
            if (bankerBetBox.value) bankerBetBox.value.resetBetUI()
            if (playerBetBox.value) playerBetBox.value.resetBetUI()
            callUpdateCallback()
        }

        // Place a single total chip only when the box has no chips yet
        // (initial load). Never rebuild after confirm — that would collapse
        // the temp stack into one custom chip.
        const ensureConfirmedChipUI = (
            box: typeof bankerBetBox.value,
            betType: number,
            amount: number
        ) => {
            if (!box || amount <= 0) return
            if (box.hasChips()) return

            box.handleOnPlaceChip({
                t: betType,
                amt: amount,
                type: 'confirmed'
            })
        }

        const syncConfirmedBetsFromTable = () => {
            if (!props.mData) return

            const { player, banker } = getMemberSideBetAmounts(
                props.mData.table,
                memberId.value
            )
            bBet.value = banker
            pBet.value = player

            // Match ctrl-bet-box-group: betFormList updates amounts only.
            // Chip stacks stay as placed / applyTempUIBet; rebuild only if empty.
            ensureConfirmedChipUI(bankerBetBox.value, BetType.BANKER, banker)
            ensureConfirmedChipUI(playerBetBox.value, BetType.PLAYER, player)
            callUpdateCallback()
        }

        // Reset local chips/bets when socket starts a new round for this table
        watch(
            () => {
                const expand = props.mData?.table?.expand
                if (!expand) return ''
                return `${expand.shoeNo}-${expand.juNo}-${expand.gameNo}`
            },
            (next, prev) => {
                if (!prev || !next || next === prev) return
                resetAllBets()
            }
        )

        // Sync confirmed bet amounts from socket / table betFormList
        watch(
            () => props.mData?.table?.expand?.betFormList,
            () => {
                syncConfirmedBetsFromTable()
            },
            { deep: true, immediate: true }
        )

        // Bet boxes may not be ready on immediate watch — paint initial chips after mount
        onMounted(() => {
            nextTick(() => {
                syncConfirmedBetsFromTable()
            })
        })

        const placeBet = (_betType: number) => {
            // check if user can bet
            if (!props.mData || isBetOnProcess.value) return

            const { mData } = props

            if (userCommandType.value === 1) {
                if (mData.table.expand.callGunner === 2) {
                    // client betting not allowed if value is 2
                    handleToastMessage(t('prompts.betting.gunnerbetmode'))
                    return
                }

                if (mData.table.expand.stopBet === 1) {
                    handleToastMessage(t('prompts.betting.nobetallowed'))
                    return
                }

                if (mData.table.expand.openPoke === 1) {
                    handleToastMessage(
                        t('prompts.betting.openisconfirmednomorebets')
                    )
                    return
                }
            }

            if (isFreeUser.value) {
                handleToastMessage(t('prompts.betting.gunnerbetmode'))
                return
            }

            // check if its allowed to bet
            if (mData.table.status !== 1 && userCommandType.value !== 1) {
                // countdown timer is done, no betting allowed
                handleToastMessage(t('prompts.betting.nobetallowed'))
                return
            }

            const _amount = chipValue.value || 0
            if (_amount > 0) {
                switch (_betType) {
                    case BetType.BANKER:
                    case BetType.DRAGON:
                        tempBBet.value += _amount
                        break
                    case BetType.PLAYER:
                    case BetType.TIGER:
                        tempPBet.value += _amount
                        break
                }

                if (
                    (_betType === BetType.BANKER ||
                        _betType === BetType.DRAGON) &&
                    bankerBetBox.value
                ) {
                    bankerBetBox.value.handleOnPlaceChip({
                        t: BetType.BANKER,
                        amt: _amount,
                        type: 'temp'
                    })
                } else if (
                    (_betType === BetType.PLAYER ||
                        _betType === BetType.TIGER) &&
                    playerBetBox.value
                ) {
                    playerBetBox.value.handleOnPlaceChip({
                        t: BetType.PLAYER,
                        amt: _amount,
                        type: 'temp'
                    })
                }

                audioPlayer.Play(SOUNDKEY.BET_PLACE)
            }

            callUpdateCallback()
        }

        const resetTempBet = () => {
            tempPBet.value = 0
            tempBBet.value = 0

            if (bankerBetBox.value) bankerBetBox.value.removeTempUIBet()
            if (playerBetBox.value) playerBetBox.value.removeTempUIBet()

            callUpdateCallback()
        }

        // Clear unconfirmed bets when countdown ends / betting stops
        watch(
            () => ({
                betTime: props.mData?.table?.expand?.daoJiShiBetTime ?? -1,
                status: props.mData?.table?.status,
                stopBet: props.mData?.table?.expand?.stopBet
            }),
            (next, prev) => {
                if (!prev) return

                const countdownEnded =
                    prev.betTime >= 0 && next.betTime < 0
                const statusStopped =
                    prev.status === 1 && next.status !== 1
                const stopBetOn =
                    prev.stopBet !== 1 && next.stopBet === 1

                if (countdownEnded || statusStopped || stopBetOn) {
                    if (tempPBet.value + tempBBet.value > 0) {
                        resetTempBet()
                    }
                }
            }
        )

        const applyTempBet = () => {
            tempPBet.value = 0
            tempBBet.value = 0

            if (bankerBetBox.value) bankerBetBox.value.applyTempUIBet()
            if (playerBetBox.value) playerBetBox.value.applyTempUIBet()

            callUpdateCallback()
        }

        const confirmTempBet = () => {
            if (isBetOnProcess.value) return
            if (!props.mData) return
            if (tempBBet.value + tempPBet.value <= 0) return

            const table = props.mData.table
            const gameType = table.gameTypeNo
            let _params: Record<string, string | number> = {
                memberId: memberId.value,
                tableId: table.id,
                shoeNo: table.expand.shoeNo,
                juNo: table.expand.juNo,
                gameNo: table.expand.gameNo,
                token: token.value
            }

            if (gameType === 'G002') {
                _params.xian = tempPBet.value
                // Prefer existing 庄 / 免佣庄 bet; fall back to props.isFreeBaccarat
                if (isEffectiveFreeBaccarat.value) {
                    _params.zhuangFree = tempBBet.value
                } else {
                    _params.zhuang = tempBBet.value
                }
            } else if (gameType === 'G003') {
                // dragon -> zhuang, tiger -> xian (same as ctrl-bet-box-group)
                _params.zhuang = tempBBet.value
                _params.xian = tempPBet.value
            } else {
                return
            }

            store.commit(AppMutation.SHOW_LOADING)
            isBetOnProcess.value = true

            saveBet(_params)
                .then((response) => {
                    store.commit(AppMutation.HIDE_LOADING)
                    isBetOnProcess.value = false

                    if (response.data.status === 100) {
                        handleToastMessage(response.data.description)
                        resetTempBet()
                    } else if (response.data.status === 200) {
                        handleToastMessage(t('prompts.betting.betsuccess'))
                        audioPlayer.Play(SOUNDKEY.BET_SUCCESS)

                        // Clear temp only — confirmed amounts come from betFormList sync
                        // (same as ctrl-bet-box-group). Adding temp into bBet/pBet here
                        // double-counts when the socket update already set them.
                        applyTempBet()
                    }
                })
                .catch(() => {
                    store.commit(AppMutation.HIDE_LOADING)
                    isBetOnProcess.value = false
                    resetTempBet()
                    handleToastMessage(t('prompts.networkerror'))
                })
        }

        const cancelTempBet = () => {
            if (isBetOnProcess.value) return
            resetTempBet()
        }

        const bankerTotal = computed((): number => {
            return props.mData?.table?.expand?.zhuangAmount ?? 0
        })

        const playerTotal = computed((): number => {
            return props.mData?.table?.expand?.xianAmount ?? 0
        })

        const chipValue = computed(() => {
            if (currentChip.value === 22 && customChip.value > 0) {
                return customChip.value
            }

            if (currentChip.value !== -1) {
                return chips.value.value[currentChip.value]
            }

            return 0
        })

        const betTime = computed((): number => {
            return props.mData?.table.expand?.daoJiShiBetTime ?? -1
        })

        const tableName = computed(() => {
            if (!props.mData) return ''
            const { tableNo, commandType } = props.mData.table

            let cmd = ''
            if (commandType === 0) cmd = t('loby.universal')
            else if (commandType === 1) cmd = t('loby.phonemode')
            else if (commandType === 2) cmd = t('loby.commandmode')

            if (props.mData.table.gameTypeNo.toUpperCase() === 'G002')
                return `${t('loby.baccarattable')}${tableNo}(${cmd})`
            else return `${t('loby.dragontigertable')}${tableNo}(${cmd})`
        })

        const timerLabel = computed((): string => {
            if (betTime.value >= 0) return String(betTime.value)
            else return '结算中'
            // 结算中
            // return ''
        })

        const tableLimit = computed(() => {
            if (!props.mData) return ''

            return `${props.mData.table.downLimit}-${props.mData.table.upLimit}`
        })

        // G002: if member already has 庄 or 免佣庄, that locks the mode;
        // otherwise follow props.isFreeBaccarat (global free/normal toggle).
        const isEffectiveFreeBaccarat = computed((): boolean => {
            if (props.mData?.table?.gameTypeNo !== 'G002') {
                return props.isFreeBaccarat
            }

            const list = props.mData?.table?.expand?.betFormList || []
            let hasNormalBanker = false
            let hasFreeBanker = false

            list.forEach((betInfo) => {
                if (betInfo.memberId !== memberId.value) return
                if (betInfo.amount <= 0) return

                if (betInfo.playName === '庄') {
                    hasNormalBanker = true
                } else if (betInfo.playName === '免佣庄') {
                    hasFreeBanker = true
                }
            })

            if (hasFreeBanker && !hasNormalBanker) return true
            if (hasNormalBanker && !hasFreeBanker) return false
            // Neither (or both unexpectedly): use global free-baccarat prop
            return props.isFreeBaccarat
        })

        const redBetRatio = computed(() => {
            if (props.mData?.table.gameTypeNo === 'G002') {
                return isEffectiveFreeBaccarat.value ? '1:1' : '1:0.95'
            }
            else return '1:1'
        })

        const gameTypeNo = computed(() => props.mData?.table.gameTypeNo ?? '')

        const deviceOrientation = computed(
            (): 'landscape' | 'portrait' => store.getters['deviceOrientation']
        )

        // Chip area size for portrait bet boxes (matches betting-group height)
        const betBoxMWidth = computed(() => 150)
        const betBoxMHeight = computed(() => 36)

        return {
            gameTypeNo,
            allowBet,
            isBetOnProcess,
            deviceOrientation,
            betBoxMWidth,
            betBoxMHeight,
            pBet,
            bBet,
            tempPBet,
            tempBBet,
            BetType,
            tableName,
            tableLimit,
            timerLabel,
            betTime,
            playerTotal,
            bankerTotal,
            redBetRatio,
            placeBet,
            confirmTempBet,
            cancelTempBet,
            bankerBetBox,
            playerBetBox,
            toastMessage,
            showCardResult,
            cardResultSnapshot,
            redCardsDisplay,
            blueCardsDisplay,
            blueLabel,
            redLabel,
            isBlueWin,
            isRedWin,
            resultLabel,
            winnerColor,
            cardClass
        }
    }
})
